import axios from 'axios';
// config
import { HOST_API } from '../config';

// ----------------------------------------------------------------------

const CACHE_TTL = 30_000;
const MAX_CACHE_ENTRIES = 100;
const CACHE_STORAGE_KEY = 'b2moon-api-cache-v1';
export const LONG_CACHE_TTL = 5 * 60_000;
const requestCache = new Map();
const CACHE_ENTRY = Symbol('cacheEntry');
let cacheHydrated = false;

function cacheStorage() {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function hydrateApiCache() {
  if (cacheHydrated) return;
  cacheHydrated = true;
  const storage = cacheStorage();
  if (!storage) return;

  try {
    const saved = JSON.parse(storage.getItem(CACHE_STORAGE_KEY) || '{}');
    const now = Date.now();
    Object.entries(saved).forEach(([key, entry]) => {
      if (entry?.expiresAt > now) {
        requestCache.set(key, {
          expiresAt: entry.expiresAt,
          persist: true,
          promise: Promise.resolve(entry.value),
          value: entry.value,
        });
      }
    });
  } catch {
    storage.removeItem(CACHE_STORAGE_KEY);
  }
}

function persistApiCache() {
  const storage = cacheStorage();
  if (!storage) return;
  const now = Date.now();
  const saved = [];
  requestCache.forEach((entry, key) => {
    if (entry.persist && entry.expiresAt > now && Object.prototype.hasOwnProperty.call(entry, 'value')) {
      saved.push([key, { expiresAt: entry.expiresAt, value: entry.value }]);
    }
  });

  while (saved.length) {
    try {
      storage.setItem(CACHE_STORAGE_KEY, JSON.stringify(Object.fromEntries(saved)));
      return;
    } catch {
      const [key] = saved.shift();
      const entry = requestCache.get(key);
      if (entry) entry.persist = false;
    }
  }
  storage.removeItem(CACHE_STORAGE_KEY);
}

export function clearApiCache() {
  cacheHydrated = true;
  requestCache.clear();
  cacheStorage()?.removeItem(CACHE_STORAGE_KEY);
}

export function cacheApiRequest(key, request, ttl = CACHE_TTL, persist = true) {
  hydrateApiCache();
  const now = Date.now();
  const cached = requestCache.get(key);
  if (cached?.expiresAt > now) return cached.promise;

  requestCache.delete(key);
  if (requestCache.size >= MAX_CACHE_ENTRIES) {
    requestCache.delete(requestCache.keys().next().value);
  }

  const entry = { expiresAt: now + ttl, persist, promise: null };
  entry.promise = Promise.resolve()
    .then(request)
    .then((value) => {
      if (requestCache.get(key) === entry) {
        entry.value = value;
        if (persist) persistApiCache();
      }
      return value;
    })
    .catch((error) => {
      if (requestCache.get(key) === entry) {
        requestCache.delete(key);
        if (persist) persistApiCache();
      }
      throw error;
    });
  requestCache.set(key, entry);
  return entry.promise;
}

function axiosCacheKey(config) {
  const authorization = config.headers?.Authorization || config.headers?.authorization || '';
  return `axios|${authorization}|${config.responseType || ''}|${axios.getUri(config)}`;
}

export function cacheResponseForParams(response, params) {
  const entry = response[CACHE_ENTRY];
  // A write may have invalidated this response while it was in flight.
  if (!entry || requestCache.get(axiosCacheKey(response.config)) !== entry) return;
  const key = axiosCacheKey({...response.config, params});
  requestCache.delete(key);
  if (requestCache.size >= MAX_CACHE_ENTRIES) requestCache.delete(requestCache.keys().next().value);
  requestCache.set(key, entry);
  persistApiCache();
}

export function cacheAdapter(adapter) {
  return async (config) => {
    const method = String(config.method || 'get').toLowerCase();
    if (method !== 'get') {
      clearApiCache();
      const response = await adapter(config);
      clearApiCache();
      return response;
    }

    if (config.cache === false || config.signal) return adapter(config);

    const key = axiosCacheKey(config);
    const ttl = Number.isFinite(config.cacheTtl) ? Math.max(0, config.cacheTtl) : CACHE_TTL;
    const persist = !config.responseType || ['json', 'text'].includes(config.responseType);
    const pending = cacheApiRequest(key, async () => {
      const fresh = await adapter(config);
      return {
        data: fresh.data,
        status: fresh.status,
        statusText: fresh.statusText,
        headers: fresh.headers,
      };
    }, ttl, persist);
    const entry = requestCache.get(key);
    const response = await pending;
    return { ...response, config, [CACHE_ENTRY]: entry };
  };
}

const axiosInstance = axios.create({
  baseURL: HOST_API,
  adapter: cacheAdapter(axios.defaults.adapter),
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;
